import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useBeforeUnload } from 'react-router-dom'
import { SETTINGS_KEYS } from '../../config/environment'
import { useSetting } from '../../hooks/useSettings'
import { openDocumentsService } from '../../services/open-documents.service'
import { FileDrop } from '../FileDrop/FileDrop'
import './AppFrame.scss'
import { Navbar } from './Navbar'
import { Sidebar } from './Sidebar'

export const AppFrame: React.FC = () => {
  const { t } = useTranslation()
  const slimSidebar = useSetting<boolean>(SETTINGS_KEYS.SLIM_SIDEBAR, false)

  // Prevent browser/tab close with unsaved changes (like React @HostListener('window:beforeunload'))
  useBeforeUnload(
    React.useCallback(() => {
      if (openDocumentsService.hasDirty()) {
        // Return value triggers browser's default beforeunload dialog
        return t('warning_you_have_unsaved_changes_to_your_documents')
      }
      return undefined // Explicitly return undefined when no dirty documents
    }, [t])
  )

  // Also handle beforeunload event directly for better browser support
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (openDocumentsService.hasDirty()) {
        e.preventDefault()
        // Chrome requires returnValue to be set
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [])

  return (
    <FileDrop>
      <div className={`app-shell ${slimSidebar ? 'app-shell--sidebar-slim' : ''}`}>
        <div className="app-shell__chrome">
          <Navbar />

          <div className="app-shell__body">
            <Sidebar />
            <main role="main" className="app-shell__content">
              <div className="app-shell__content-surface">
                <Outlet />
              </div>
            </main>
          </div>
        </div>
      </div>
    </FileDrop>
  )
}
